const { jQuery: $ } = window;

/**
 * Renders post cards via the Mustache template and animates them into the DOM. transitionPosts() does not
 * mutate `filterPage` — callers set the page explicitly.
 */
export default class Renderer {
    /** @member {Hooks} */
    #hooks;

    /** @member {Logger} */
    #logger;

    /** @member {Mustache} */
    #mustache;

    /**
     * @param {Hooks} hooks
     * @param {Logger} logger
     * @param {Mustache} mustache
     */
    constructor(hooks, logger, mustache) {
        this.#hooks = hooks;
        this.#logger = logger;
        this.#mustache = mustache;
    }

    /**
     * Renders a single post using the Mustache template embedded in the shortcode
     * (`<script class="kpfsc__template">`). Filterable via post_template / post_data / render_post_output.
     *
     * @param {jQuery} $el The shortcode element the post will be rendered in.
     * @param {object} post
     * @returns {string} Rendered HTML.
     */
    renderPost($el, post) {
        const template = this.#hooks.applyFilter("post_template", $el.find(".kpfsc__template").text(), $el, post);

        let output = this.#mustache.render(template, this.#hooks.applyFilter("post_data", post, $el));

        return this.#hooks.applyFilter("render_post_output", output, $el, post);
    }

    /**
     * Adds posts to the DOM with a staggered delay so CSS transitions can animate them in. Filter
     * post_transition_delay to adjust (0 disables). Fires posts_transition_finished when complete.
     *
     * Cancelable timeouts are tracked per shortcode instance so clearing one shortcode never interferes
     * with another.
     *
     * @param {ShortcodeInstance} instance The shortcode instance being rendered into.
     * @param {array} posts
     * @param {boolean} clear Whether to clear the posts container first.
     * @param {int} delay Milliseconds between each post's entry.
     * @param {Function} done Called once all posts finish transitioning.
     * @param {boolean} animate Animate the entry (default). False renders instantly, fully settled — used on
     *                          a history restore, where a staggered fade-in "after load" looks wrong.
     */
    transitionPosts(instance, posts, clear = false, delay = 150, done = null, animate = true) {
        const $el = instance.$el;
        const $posts = $el.find(".kpfsc__posts");
        delay = this.#hooks.applyFilter("post_transition_delay", delay, $el);

        if(clear) {
            // Cancel every pending transition timeout for this instance (pop-drain — a for..in over the
            // shrinking array would skip half).
            while(instance.transitions.length) {
                clearTimeout(instance.transitions.pop());
            }

            $posts.html("");
        }

        if(!done) {
            done = () => { this.#logger.log("Finished Transitioning Posts."); };
        }

        instance.transitions = [];

        // Instant (un-animated) render — used when restoring a history entry. Each post is appended fully
        // settled: inline animation:none + opacity:1 override the template's own entrance animation
        // (template-agnostic; inline beats any non-important CSS), and the render finishes synchronously.
        if(!animate) {
            for(const post of posts) {
                $(this.renderPost($el, post)).css({ animation: "none", opacity: 1 }).appendTo($posts);
            }

            $posts.attr("aria-busy", "false");
            done($el);
            this.#hooks.doAction("posts_transition_finished", $el);
            return;
        }

        // Mark the results region busy while it re-renders (a11y). Cleared when the last post settles, or
        // immediately when there are no posts to render.
        $posts.attr("aria-busy", "true");
        if(posts.length === 0) {
            $posts.attr("aria-busy", "false");
        }

        let counter = 0;
        let remaining = posts.length;

        for(const post of posts) {
            const $post = $(this.renderPost($el, post)).addClass("kpfsc__post--transitioning");
            $posts.append($post);

            // Store the timeout so it can be cancelled if a clear happens mid-animation.
            instance.transitions.push(setTimeout(() => {
                $post.removeClass("kpfsc__post--transitioning");

                remaining--;

                if(remaining === 0) {
                    $posts.attr("aria-busy", "false");
                    done($el);
                    this.#hooks.doAction("posts_transition_finished", $el);
                }
            }, counter * delay));
            counter++;
        }
    }
}
